import React, { useEffect, useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  SelectInput,
  ListProps,
  useNotify,
  useRefresh,
  TopToolbar,
  FilterButton,
} from 'react-admin';
import { Button } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import useAuthorize from '../hooks/useAuthorize';
import { LEADS_MODULE_ROLES } from '../roles/portalRoles';
import { processLeadEnrichmentBatch, retryFailedLeadJobs, getLeadEnrichmentPhase, queueLeadEnrichmentPhase } from './leadsApi';
import EnrichmentPhaseBanner, { EnrichmentPhaseInfo } from './EnrichmentPhaseBanner';

const filters = [
  <SelectInput
    label="Status"
    source="status"
    key="status"
    alwaysOn
    resettable
    choices={[
      { id: 'pending', name: 'Pending' },
      { id: 'processing', name: 'Processing' },
      { id: 'completed', name: 'Completed' },
      { id: 'failed', name: 'Failed' },
    ]}
  />,
];

const JobActions = () => {
  const notify = useNotify();
  const refresh = useRefresh();

  const runBatch = async () => {
    try {
      const result = await processLeadEnrichmentBatch(25);
      notify(`Processed ${result.processed} jobs (${result.failed} failed)`, { type: 'info' });
      refresh();
    } catch (error: any) {
      notify(error?.body?.error || error?.message || 'Batch processing failed', { type: 'error' });
    }
  };

  const retryFailed = async () => {
    try {
      const result = await retryFailedLeadJobs();
      notify(`Retried ${result.retried} failed jobs`, { type: 'info' });
      refresh();
    } catch (error: any) {
      notify(error?.body?.error || error?.message || 'Retry failed', { type: 'error' });
    }
  };

  const queuePhase = async () => {
    try {
      const result = await queueLeadEnrichmentPhase();
      notify(
        `Queued ${result.queued} jobs for ${result.enrichmentPhase?.label || 'active phase'}`,
        { type: 'info' }
      );
      refresh();
    } catch (error: any) {
      notify(error?.body?.error || error?.message || 'Phase queue failed', { type: 'error' });
    }
  };

  return (
    <TopToolbar>
      <FilterButton />
      <Button size="small" startIcon={<PlayArrowIcon />} onClick={runBatch}>
        Process batch
      </Button>
      <Button size="small" onClick={queuePhase}>
        Queue active phase
      </Button>
      <Button size="small" startIcon={<ReplayIcon />} onClick={retryFailed}>
        Retry failed
      </Button>
    </TopToolbar>
  );
};

export const LeadEnrichmentJobList: React.FC<ListProps> = (props) => {
  useAuthorize(LEADS_MODULE_ROLES);
  const [enrichmentPhase, setEnrichmentPhase] = useState<EnrichmentPhaseInfo | null>(null);

  useEffect(() => {
    getLeadEnrichmentPhase()
      .then(setEnrichmentPhase)
      .catch(() => setEnrichmentPhase(null));
  }, []);

  return (
    <>
      <EnrichmentPhaseBanner phase={enrichmentPhase} />
      <List
      {...props}
      resource="leadenrichmentjobs"
      filters={filters}
      sort={{ field: 'dt_updated', order: 'DESC' }}
      perPage={50}
      title="Lead Enrichment Queue"
      actions={<JobActions />}
    >
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <TextField source="id" />
        <TextField source="master_company_id" label="Lead ID" />
        <TextField source="company_name" />
        <TextField source="bucket" />
        <TextField source="source" />
        <TextField source="status" />
        <NumberField source="priority" />
        <NumberField source="attempts" />
        <TextField source="result_summary" />
        <TextField source="last_error" />
        <DateField source="scheduled_at" showTime />
        <DateField source="dt_updated" showTime />
      </Datagrid>
    </List>
    </>
  );
};

export default LeadEnrichmentJobList;
